import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  CommunicationIntegrationKind,
  KIND_TO_LINKED_CHANNEL,
} from "@/types/communication-integration";
import { useDeleteIntegration } from "../hooks/communication-integrations.hooks";

interface DeleteIntegrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hotelId: string;
  kind: CommunicationIntegrationKind;
}

export const DeleteIntegrationDialog: React.FC<
  DeleteIntegrationDialogProps
> = ({ open, onOpenChange, hotelId, kind }) => {
  const { t } = useTranslation();
  const { mutate, isPending } = useDeleteIntegration(hotelId);

  const kindLabel = t(`communicationChannels.kinds.${kind}.label`);
  const channelLabel = t(
    `communicationChannels.channels.${KIND_TO_LINKED_CHANNEL[kind]}.label`,
  );

  const handleConfirm = () => {
    mutate(kind, {
      onSuccess: () => {
        toast.success(t("communicationChannels.delete.success"), {
          description: t("communicationChannels.delete.cascadeDescription", {
            channel: channelLabel,
          }),
        });
        onOpenChange(false);
      },
      onError: () => {
        toast.error(t("communicationChannels.delete.error"));
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("communicationChannels.delete.title", { kind: kindLabel })}
          </DialogTitle>
          <DialogDescription>
            {t("communicationChannels.delete.description")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="new_outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t("communicationChannels.actions.cancel")}
          </Button>
          <Button
            type="button"
            variant="new_destructive"
            onClick={handleConfirm}
            disabled={isPending}
          >
            {t("communicationChannels.delete.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
